import React, {useEffect, useState, useCallback, useRef, useMemo} from "react";
import {
    BrowserRouter as Router, Routes, Route,
    useLocation, useNavigate, useSearchParams,
} from "react-router-dom";

import SearchBarContainer from "./search/SearchBarContainer";
import PropertyCard from "./components/PropertyCard";
import FloatingWhatsapp from "./components/FloatingWhatsapp";
import TalkToMeWidget from "./components/TalkToMeWidget";
import Footer from "./components/Footer";
import { AuthenticationPage, authenticationApi, sessionStorageService, AUTH_STORAGE_KEYS, SESSION_EXPIRED_EVENT } from "./features/authentication";
import { FavoritesProvider } from "./features/favorites/FavoritesContext";
import useClickOutside from "./search/hooks/useClickOutside";
import Icon from "./components/ai/Icon";
import SettingsModal, { PROFILE_UPDATED_EVENT } from "./components/ai/SettingsModal";
import Contact from "./pages/Contact";
import PropertyDetails from "./components/propertydetails/PropertyDetails";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import CookiePolicy from "./pages/CookiePolicy";
import CustomerSupport from "./pages/CustomerSupport";
import Careers from "./pages/Careers";
import Blog from "./pages/Blog";
import AiSearchPage from "./components/ai/AiSearchPage";
import SharedChatPage from "./components/ai/SharedChatPage";
import PersonalAgentPage from "./components/ai/PersonalAgentPage";
import AgentAdminApp from "./features/agent/AgentApp";
import BookTourPage from "./components/admindashboard/BookTourPage";
import {
    AdminPage, DashboardPage, ListingEditorPage, LoginPage, ProjectEditorPage, RegisterPage, isWorkspacePath,
} from "./features/brokerage";
import LiveJoinPage from "./components/propertydetails/LiveJoinPage";
import LiveRoomPage from "./components/propertydetails/LiveRoomPage";

import "./styles/App.css";

const BASE_URL           = process.env.REACT_APP_API_BASE;
// Hidden from the home header for now; flip to true to bring the link back (the /personal-agent route still works).
const SHOW_PERSONAL_AGENT_LINK = false;
const HIDE_FOOTER_ROUTES = new Set(["/ai-search", "/personal-agent", "/dashboard", "/admin", "/admin/brokers", "/agent-admin", "/book"]);

function MetaPixelTracker() {
    const location = useLocation();
    useEffect(() => {
        window.fbq?.("track", "PageView");
    }, [location.pathname]);
    return null;
}

/**
 * Scrolls to the top of the page on every route change (SPA navigation
 * doesn't do this automatically, unlike a normal full page load) -- or to
 * the element matching the URL hash, if one is present and exists on the
 * page (e.g. /broker/register#whatsapp).
 */
function ScrollRestoration() {
    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
            const target = document.getElementById(location.hash.slice(1));
            if (target) {
                target.scrollIntoView({behavior: "auto", block: "center"});
                return;
            }
        }
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, [location.pathname, location.search, location.hash]);
    return null;
}

/**
 * Root application component: sets up routing for the property marketplace
 * (listings, search, property details, booking, broker portal, live tours)
 * and wraps everything in the favorites context.
 */
function App() {
    return <Router><AppContent/></Router>;
}

function AppContent() {
    const location = useLocation();
    const navigate = useNavigate();

    const [properties, setProperties] = useState([]);
    const [isLoadingProperties, setIsLoadingProperties] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(
        () => Boolean(sessionStorageService.get())
    );
    const [currentUser, setCurrentUser] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // The JWT only encodes email, so the verified name/avatar for the
    // header dropdown has to come from /api/auth/me, not the token.
    useEffect(() => {
        if (!isAuthenticated) {
            setCurrentUser(null);
            return;
        }
        authenticationApi.getCurrentUser()
            .then((data) => setCurrentUser(data))
            .catch(() => setCurrentUser(null));
    }, [isAuthenticated]);

    useEffect(() => {
        const onProfileUpdated = (e) => setCurrentUser((prev) => ({ ...(prev || {}), ...e.detail }));
        window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
        return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    }, []);

    // The backend refused the stored token (expired or revoked): show the signed-out header, as Logout does.
    useEffect(() => {
        const onExpired = () => {
            setIsAuthenticated(false);
            setCurrentUser(null);
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, []);

    // Keeps this tab's auth state in sync when login/logout happens in
    // ANOTHER same-origin tab (e.g. an email confirmation link that opened
    // in a new tab) - the `storage` event fires here automatically since
    // it never fires in the tab that made the change itself.
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === AUTH_STORAGE_KEYS.TOKEN) {
                setIsAuthenticated(Boolean(e.newValue));
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const isAgentAdmin = location.pathname.startsWith("/agent-admin");
    const isLiveJoin = location.pathname.startsWith("/live/join");
    const isLiveRoom = location.pathname.startsWith("/live/") &&
        !location.pathname.startsWith("/live/join");
    const isAuthPage = location.pathname === "/auth";
    const shouldHideFooter = isAuthPage || HIDE_FOOTER_ROUTES.has(location.pathname) || isWorkspacePath(location.pathname);

    // Fetches the full listing set once (this endpoint's `size=1000` already
    // means "everything") -- search/type filtering below runs client-side
    // over this same array rather than re-hitting the network per keystroke.
    const loadProperties = useCallback(async () => {
        setIsLoadingProperties(true);
        try {
            const params = new URLSearchParams({page: 0, size: 1000});
            const res = await fetch(`${BASE_URL}/api/properties?${params}`);
            const data = await res.json();
            setProperties(data?.content ?? []);
        } catch (err) {
            console.error("Failed to fetch:", err);
            setProperties([]);
        } finally {
            setIsLoadingProperties(false);
        }
    }, []);

    // Only the home page shows this list: fetch it the first time "/" is shown and keep it for later visits, rather
    // than on every page load (a direct link to /terms or a property used to download every listing too).
    const isHome = location.pathname === "/";
    const propertiesRequestedRef = useRef(false);
    useEffect(() => {
        if (!isHome || propertiesRequestedRef.current) return;
        propertiesRequestedRef.current = true;
        loadProperties();
    }, [isHome, loadProperties]);

    const handleSearch = useCallback((query) => {
        if (query === null) {
            setIsSearching(false);
            setSearchQuery(null);
            return;
        }
        setIsSearching(true);
        setSearchQuery(query);
    }, []);

    if (isAgentAdmin) return <><MetaPixelTracker/><ScrollRestoration/><AgentAdminApp/></>;

    if (isLiveJoin) return (
        <><MetaPixelTracker/><ScrollRestoration/>
            <Routes>
                <Route path="/live/join/:requestId" element={<LiveJoinPage/>}/>
            </Routes>
        </>
    );

    if (isLiveRoom) return (
        <><MetaPixelTracker/><ScrollRestoration/>
            <Routes>
                <Route path="/live/:roomName" element={<LiveRoomPage/>}/>
            </Routes>
        </>
    );

    return (
        <>
            <MetaPixelTracker/>
            <ScrollRestoration/>
            <FavoritesProvider isAuthenticated={isAuthenticated}>
                <div className="app-container">
                    {!isAuthPage && !isWorkspacePath(location.pathname) && (
                        <Header
                            isAuthenticated={isAuthenticated}
                            currentUser={currentUser}
                            onLogout={() => {
                                sessionStorageService.remove();
                                setIsAuthenticated(false);
                                setCurrentUser(null);
                                navigate("/");
                            }}
                            onLogoClick={() => {
                                handleSearch(null);
                                navigate("/", {replace: true});
                            }}
                            onOpenSettings={() => setSettingsOpen(true)}
                        />
                    )}
                    <Routes>
                        <Route path="/"
                               element={<HomePage properties={properties} isSearching={isSearching}
                                                  searchQuery={searchQuery}
                                                  isLoading={isLoadingProperties} onSearch={handleSearch}/>}
                        />
                        <Route path="/property/:slug" element={<PropertyDetails/>}/>
                        <Route path="/book/:slug" element={<BookTourPage/>}/>
                        <Route path="/about" element={<About/>}/>
                        <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                        <Route path="/terms" element={<Terms/>}/>
                        <Route path="/cookies" element={<CookiePolicy/>}/>
                        <Route path="/support" element={<CustomerSupport/>}/>
                        <Route path="/careers" element={<Careers/>}/>
                        <Route path="/blog" element={<Blog/>}/>
                        <Route path="/contact" element={<Contact/>}/>
                        <Route path="/ai-search" element={<AiSearchPage/>}/>
                        <Route path="/shared/:token" element={<SharedChatPage/>}/>
                        <Route path="/personal-agent" element={<PersonalAgentPage/>}/>
                        <Route path="/broker/register" element={<RegisterPage/>}/>
                        <Route path="/broker/login" element={<LoginPage/>}/>
                        <Route path="/broker/dashboard" element={<DashboardPage/>}/>
                        <Route path="/broker/listings/new" element={<ListingEditorPage/>}/>
                        <Route path="/broker/listings/:id" element={<ListingEditorPage/>}/>
                        <Route path="/broker/projects/new" element={<ProjectEditorPage/>}/>
                        <Route path="/broker/projects/:id" element={<ProjectEditorPage/>}/>
                        <Route path="/admin/brokers" element={<AdminPage/>}/>
                        <Route path="/auth"
                               element={<AuthenticationPage onAuthSuccess={() => setIsAuthenticated(true)}/>}
                        />
                    </Routes>
                    {!isAuthPage && <FloatingWhatsapp/>}
                    {!shouldHideFooter && <Footer/>}
                    {isAuthenticated && (
                        <SettingsModal
                            open={settingsOpen}
                            onClose={() => setSettingsOpen(false)}
                            user={{name: currentUser?.fullName, email: currentUser?.email}}
                        />
                    )}
                </div>
            </FavoritesProvider>
        </>
    );
}

function Header({isAuthenticated, currentUser, onLogout, onLogoClick, onOpenSettings}) {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);

    const name = currentUser?.fullName || "Account";
    const email = currentUser?.email || "";
    const initials = name.charAt(0).toUpperCase();

    return (
        <header className="topbar">
            <div
                className="header-left"
                onClick={onLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onLogoClick()}
                aria-label="Go to homepage"
            >
                <img src="/logo.png" alt="OGM" className="logo-img"/>
                <span className="header-sub">One Global Marketplace</span>
            </div>
            <div className="header-actions">
                {isAuthenticated && SHOW_PERSONAL_AGENT_LINK && (
                    <button className="header-nav-link" onClick={() => navigate("/personal-agent")}>
                        Personal Agent
                    </button>
                )}
                {isAuthenticated ? (
                    <div className="header-profile" ref={menuRef}>
                        <button
                            type="button"
                            className="header-profile-trigger"
                            onClick={() => setMenuOpen((o) => !o)}
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                            aria-label={`Account menu for ${name}`}
                        >
                            {currentUser?.avatarUrl ? (
                                <img className="header-avatar header-avatar-img" src={currentUser.avatarUrl} alt=""/>
                            ) : (
                                <span className="header-avatar">{initials}</span>
                            )}
                        </button>

                        {menuOpen && (
                            <div className="header-profile-menu" role="menu">
                                <div className="header-profile-menu-info">
                                    <div className="name">{name}</div>
                                    {email && <div className="email">{email}</div>}
                                </div>
                                <div className="header-profile-menu-divider"/>
                                <button
                                    type="button"
                                    className="header-profile-menu-item"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onOpenSettings();
                                    }}
                                >
                                    <Icon name="settings" size={16}/>
                                    Account Settings
                                </button>
                                <div className="header-profile-menu-divider"/>
                                <button
                                    type="button"
                                    className="header-profile-menu-item danger"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onLogout();
                                    }}
                                >
                                    <Icon name="logout" size={16}/>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button className="contact" onClick={() => navigate("/auth")}>Login</button>
                )}
            </div>
        </header>
    );
}

// Footer "Properties" links (?type=buy|rent|projects|weekend) filter the
// already-fetched property list client-side -- there's no dedicated
// backend endpoint for these categories. "rent" always empties out: OGM
// doesn't carry rental inventory (every listing is for sale), so it's
// treated as a real, honestly-labeled category rather than faked.
const CATEGORY_META = {
    rent: {
        title: "Rental Listings",
        filter: () => false,
        emptyMessage: "We don't currently have rental listings -- every property on OGM is for sale.",
    },
    projects: {
        title: "New Projects",
        filter: (p) => (p.possessionStatus || "").toLowerCase() !== "ready_to_move",
        emptyMessage: "No new/upcoming projects match right now -- check back soon.",
    },
    weekend: {
        title: "Weekend Homes",
        filter: (p) => /holiday|weekend/i.test(p.title || ""),
        emptyMessage: "No weekend/holiday homes match right now -- check back soon.",
    },
};

function HomePage({properties, isSearching, searchQuery, isLoading, onSearch}) {
    const [searchParams] = useSearchParams();
    const category = CATEGORY_META[searchParams.get("type")];

    const visibleProperties = useMemo(() => {
        const all = properties || [];
        if (isSearching) {
            const q = searchQuery?.trim().toLowerCase();
            if (!q) return all;
            // Mirrors the backend's own `q` matching (repository.py's
            // advanced_search: title/location/description ILIKE) -- filtered
            // here instead of re-fetched, since `properties` already holds
            // every listing this endpoint would return anyway.
            return all.filter((p) =>
                p.title?.toLowerCase().includes(q) ||
                p.location?.toLowerCase().includes(q) ||
                p.description?.toLowerCase().includes(q)
            );
        }
        return category ? all.filter(category.filter) : all;
    }, [properties, isSearching, searchQuery, category]);

    const title = isSearching ? "Search Results" : (category?.title || "Popular Homes in Bengaluru");

    return (
        <main className="main-section">
            <SearchBarContainer onSearch={onSearch}/>
            <h2 className="section-title">
        <span className="section-title-text">{title}</span>
            </h2>
            <PropertyList properties={visibleProperties} isLoading={isLoading} emptyMessage={category?.emptyMessage}/>
            {/* Floating entry point into the AI experience. Portals itself
                to <body>, so its position here is scoping only: the pill
                belongs to the homepage and nowhere else. */}
            <TalkToMeWidget/>
        </main>
    );
}

function PropertyCardSkeleton() {
    return (
        <div className="pc pc-skeleton" aria-hidden="true">
            <div className="pc-img-wrap skel"/>
            <div className="pc-body">
                <div className="skel" style={{height: 14, width: "70%", marginBottom: 8}}/>
                <div className="skel" style={{height: 11, width: "45%", marginBottom: 10}}/>
                <div className="skel" style={{height: 24, width: "100%", borderRadius: 999}}/>
            </div>
        </div>
    );
}

function PropertyList({properties, isLoading, emptyMessage}) {
    const navigate = useNavigate();
    if (isLoading) {
        return (
            <div className="property-grid">
                {Array.from({length: 10}, (_, i) => <PropertyCardSkeleton key={i}/>)}
            </div>
        );
    }
    if (!properties?.length) {
        return (
            <div className="no-results-container">
                <div className="no-results-content">
                    <span className="no-results-icon">🏠</span>
                    <h3>No properties found</h3>
                    <p>{emptyMessage || "We couldn't find any listings matching your criteria."}</p>
                    <button className="reset-search-btn" onClick={() => navigate("/")}>
                        View all properties
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="property-grid">
            {properties.map((prop) => <PropertyCard key={prop.id} property={prop}/>)}
        </div>
    );
}

export default App;